import { logger } from '@/utils/logger';

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UserRole } from './useCurrentUserRole';
import { useAuth } from '@/contexts/AuthContext';

export interface UserWithRole {
  id: string;
  email: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  mobile_number: string | null;
  time_zone: string | null;
  role: UserRole;
  created_at: string;
  active: boolean;
  two_factor_enabled: boolean | null;
  email_confirmed_at: string | null;
  is_verified: boolean;
  status: string;
}

// Helper function to get display name for sorting
const getUserDisplayName = (user: UserWithRole) => {
  return user.first_name && user.last_name 
    ? `${user.first_name} ${user.last_name}`
    : user.full_name || 'No name provided';
};

export const useUsersWithRoles = (currentUserRole: UserRole | null) => {
  const { activeOrganizationId } = useAuth();

  return useQuery({
    queryKey: ['usersWithRoles', activeOrganizationId],
    queryFn: async () => {
      logger.debug('Fetching users with roles...');
      
      if (!activeOrganizationId) {
        throw new Error('No active organization selected');
      }

      const { data: memberships, error: membershipsError } = await supabase
        .from('organization_members' as any)
        .select('user_id, role, status, two_factor_enabled, created_at')
        .eq('organization_id', activeOrganizationId) as { data: Array<{ user_id: string; role: UserRole; status: string; two_factor_enabled: boolean | null; created_at: string }> | null; error: any };

      logger.debug('Memberships query result:', memberships, 'Error:', membershipsError);

      if (membershipsError) {
        logger.error('Error fetching organization memberships:', membershipsError);
        throw membershipsError;
      }

      if (!memberships || memberships.length === 0) {
        logger.debug('No memberships found in organization');
        return [];
      }

      const userIds = memberships.map((membership) => membership.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('v_profiles_admin' as any)
        .select('id, email, full_name, first_name, last_name, mobile_number, time_zone')
        .in('id', userIds) as { data: Array<{ id: string; email: string | null; full_name: string | null; first_name: string | null; last_name: string | null; mobile_number: string | null; time_zone: string | null }> | null; error: any };

      if (profilesError) {
        logger.error('Error fetching admin profile view:', profilesError);
        throw profilesError;
      }

      const profilesById = new Map((profiles || []).filter((profile) => profile.id).map((profile) => [profile.id, profile]));

      // Get user roles for each profile
      const usersWithRoles = memberships
        .map((membership) => {
          const profile = profilesById.get(membership.user_id);
          if (!profile) {
            logger.warn('No profile found for membership user:', membership.user_id);
            return null;
          }
          logger.debug('Processing profile:', profile.id, profile.email);
          // Use the status from the profiles table to determine verification
          const isVerified = membership.status === 'Active';

          const userWithRole: UserWithRole = {
            id: profile.id,
            email: profile.email || '',
            full_name: profile.full_name || '',
            first_name: profile.first_name,
            last_name: profile.last_name,
            mobile_number: profile.mobile_number,
            time_zone: profile.time_zone,
            role: membership.role,
            active: true,
            created_at: membership.created_at,
            two_factor_enabled: membership.two_factor_enabled,
            email_confirmed_at: isVerified ? membership.created_at : null,
            is_verified: isVerified,
            status: membership.status
          };
          
          logger.debug('Final user object:', userWithRole);
          return userWithRole;
        })
        .filter((user): user is UserWithRole => user !== null);

      // Sort users alphabetically by display name
      const sortedUsers = usersWithRoles.sort((a, b) => {
        const nameA = getUserDisplayName(a).toLowerCase();
        const nameB = getUserDisplayName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });

      logger.debug('Final users with roles (sorted):', sortedUsers);
      return sortedUsers;
    },
    enabled: currentUserRole === 'admin' && !!activeOrganizationId
  });
};
